<div class="rp-wrap">
  <!-- HERO -->
  <header class="rp-hero" role="banner">
    <div class="rp-hero-glow" aria-hidden="true"></div>
    <div class="rp-eyebrow">
      <span aria-hidden="true">💳</span> Refund Policy
    </div>
    <h1 class="rp-title">Refund <span class="text-accent">Policy</span></h1>
    <p class="rp-sub">
      This policy explains how
      <strong>Pythrust Technologies OPC Private Limited</strong>
      handles refunds, credit considerations, and billing disputes for
      <strong>Angularize.dev</strong>. Please read it carefully before
      purchasing any subscription or consuming platform credits.
    </p>
    <div class="rp-meta">
      <div class="meta-pill"><strong>Effective:</strong> {{ lastUpdated }}</div>
      <div class="meta-pill">
        <strong>Owner:</strong> Pythrust Technologies OPC Pvt. Ltd.
      </div>
      <div class="meta-pill">
        <strong>Jurisdiction:</strong> Gurugram, Haryana, India
      </div>
      <div class="meta-pill">
        Also see:
        <a routerLink="/terms-and-conditions">Terms</a>
        &amp;
        <a routerLink="/privacy-policy">Privacy</a>
      </div>
    </div>
  </header>

  <!-- BODY -->
  <div class="rp-layout">
    <!-- SIDEBAR TOC -->
    <aside class="toc" aria-label="Table of Contents">
      <p class="toc-label">Contents</p>
      <ul class="toc-list">
        <li *ngFor="let s of tocSections">
          <a
            [class.active]="s.active"
            (click)="scrollTo(s.id)"
            (keydown.enter)="scrollTo(s.id)"
            tabindex="0"
            [attr.aria-current]="s.active ? 'location' : null"
            >{{ s.label }}</a
          >
        </li>
      </ul>
    </aside>

    <!-- MAIN CONTENT -->
    <main class="rp-content" role="main">
      <!-- ── R1: INTRODUCTION ── -->
      <section class="sec" id="r1">
        <span class="sec-num">Section 01</span>
        <h2 class="sec-heading">Introduction</h2>
        <p>
          This Refund Policy ("<strong>Policy</strong>") governs all payment,
          subscription, and credit matters relating to
          <strong>Angularize.dev</strong>, operated by
          <strong>Pythrust Technologies OPC Private Limited</strong>
          ("<strong>Company</strong>", "<strong>we</strong>",
          "<strong>us</strong>", or "<strong>our</strong>").
        </p>
        <p>
          By subscribing to any paid plan or consuming any platform credits on
          <strong>angularize.dev</strong> or
          <strong>beta.angularize.dev</strong>, you acknowledge and agree to the
          terms of this Policy. This Policy forms part of, and must be read
          together with, our
          <a routerLink="/terms-and-conditions">Terms of Service</a> and
          <a routerLink="/privacy-policy">Privacy Policy</a>.
        </p>
        <div class="callout callout-warn">
          <strong>⚠ Core Stance</strong>
          All payments made to Angularize.dev are
          <strong>non-refundable by default</strong>, in both monetary and
          credit terms. The limited exceptions and discretionary credit
          considerations described in this Policy do not create a right to a
          refund or credit under any circumstance.
        </div>
      </section>

      <!-- ── R2: GENERAL NO-REFUND RULE ── -->
      <section class="sec" id="r2">
        <span class="sec-num">Section 02</span>
        <h2 class="sec-heading">General No-Refund Rule</h2>
        <p>
          Angularize.dev is a software-as-a-service ("<strong>SaaS</strong>")
          platform. Access to the platform, its AI-powered code generation
          features, and associated computational resources constitutes immediate
          delivery of a digital service upon subscription or credit purchase.
          Accordingly:
        </p>
        <ul class="doc-list">
          <li>
            <strong>All subscription fees are non-refundable</strong>, including
            monthly and annual plans, regardless of actual usage during the
            billing period;
          </li>
          <li>
            <strong>All credit purchases are non-refundable</strong> once
            credits have been added to your account, whether or not those
            credits have been consumed;
          </li>
          <li>
            <strong>Partial billing periods are non-refundable</strong> — if you
            cancel mid-cycle, you retain access until the period ends but
            receive no refund for unused days;
          </li>
          <li>
            <strong>Unused credits expire</strong> as set out in your
            subscription plan terms and are not redeemable for cash under any
            circumstance;
          </li>
          <li>
            <strong>Downgrades</strong> from a higher to a lower plan take
            effect at the next billing cycle; no pro-rata refund is issued for
            the difference in the current period.
          </li>
        </ul>
        <div class="callout callout-neutral">
          <strong>📌 Why No Refunds?</strong>
          Upon access, the Platform immediately provides value through
          infrastructure, AI model inference, code generation compute, and
          platform availability — costs that are incurred by us regardless of
          the volume of prompts you submit. This policy reflects the nature of
          digital SaaS delivery under Indian consumer law and the IT Act, 2000.
        </div>
      </section>

      <!-- ── R3: CREDIT CONSIDERATION ── -->
      <section class="sec" id="r3">
        <span class="sec-num">Section 03</span>
        <h2 class="sec-heading">Discretionary Credit Consideration</h2>
        <p>
          While monetary refunds are not issued, the Company may, entirely at
          its own discretion, consider issuing
          <strong>platform credits</strong> (not cash) in a narrow set of
          circumstances. A credit consideration is <strong>not a right</strong>
          — it is a goodwill gesture that the Company may extend after a
          thorough internal review.
        </p>

        <h3 class="sub-heading">
          3.1 Eligible Scenario — Prompt Submitted, No Meaningful Output
        </h3>
        <p>
          We may consider a credit if <strong>all</strong> of the following
          conditions are met:
        </p>
        <ul class="doc-list">
          <li>
            You submitted a well-formed, unambiguous prompt to the Platform and
            credits were deducted from your account;
          </li>
          <li>
            The Platform returned <strong>no output whatsoever</strong> — i.e. a
            complete failure to generate any response, not a response you
            consider low-quality or incorrect;
          </li>
          <li>
            The failure is attributable to a
            <strong>confirmed technical fault on our infrastructure</strong> —
            such as a server error (HTTP 5xx), a timed-out generation request,
            or a verified platform outage — and <strong>not</strong> to
            third-party AI model behaviour, LLM hallucination, or model
            uncertainty;
          </li>
          <li>
            You report the issue to us within <strong>7 days</strong> of the
            failed generation with supporting evidence (screenshots, request
            IDs, timestamps, and the exact prompt submitted);
          </li>
          <li>
            Our internal review of server logs and generation records
            <strong>confirms</strong>
            the failure and attributes it to a platform-side fault.
          </li>
        </ul>

        <h3 class="sub-heading">
          3.2 What "No Meaningful Change" Does Not Mean
        </h3>
        <p>
          The following are <strong>explicitly not grounds</strong> for any
          credit or refund, even if you are dissatisfied with the output:
        </p>
        <ul class="doc-list">
          <li>
            The generated code does not match your expectations, aesthetic
            preferences, or unstated requirements;
          </li>
          <li>
            The output requires further refinement, iteration, or manual
            editing;
          </li>
          <li>
            The generated code contains bugs, errors, or security issues (see
            Section 4 — LLM limitations);
          </li>
          <li>
            The prompt was ambiguous, incomplete, or did not fully describe the
            desired outcome;
          </li>
          <li>
            You submitted multiple prompts iterating on the same feature and did
            not achieve the result within a particular number of credits;
          </li>
          <li>
            The output is different from a prior generation for a similar prompt
            (LLMs are non-deterministic by nature).
          </li>
        </ul>

        <h3 class="sub-heading">3.3 Credit Value &amp; Expiry</h3>
        <p>
          Credits issued as a goodwill gesture under this Section are platform
          credits only — they carry no monetary value, cannot be withdrawn as
          cash, cannot be transferred to another account, and expire at the end
          of the billing cycle in which they are issued, or within
          <strong>30 days</strong> of issuance, whichever is sooner.
        </p>

        <div class="callout callout-info">
          <strong>ℹ Review Timeline</strong>
          Credit consideration requests are reviewed within
          <strong>14 business days</strong>
          of receipt of all required evidence. We reserve the right to decline
          any request without providing reasons, and our decision is final.
        </div>
      </section>

      <!-- ── R4: LLM UNCERTAINTY ── -->
      <section class="sec" id="r4">
        <span class="sec-num">Section 04</span>
        <h2 class="sec-heading">
          LLM Uncertainty, Hallucination &amp; Non-Determinism
        </h2>

        <div class="callout callout-warn">
          <strong>⚠ Important — Please Read</strong>
          The core generation engine of Angularize.dev is powered by large
          language models (LLMs). LLMs are probabilistic systems with inherent
          limitations. Credits consumed during any LLM inference call are
          <strong>non-refundable and non-creditable</strong>
          regardless of output quality.
        </div>

        <h3 class="sub-heading">4.1 Nature of LLMs</h3>
        <p>
          Large language models, by their fundamental design, exhibit the
          following characteristics that you must understand and accept before
          using the Platform:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Hallucination:</strong> LLMs may generate plausible-sounding
            but factually incorrect, technically wrong, or entirely fabricated
            code, function names, library references, or configuration values;
          </li>
          <li>
            <strong>Non-determinism:</strong> The same prompt submitted twice
            may produce materially different outputs due to temperature and
            sampling parameters inherent to LLM inference;
          </li>
          <li>
            <strong>Context limitations:</strong> LLMs operate within a fixed
            context window. Large codebases or complex multi-file prompts may
            result in truncated, incomplete, or inconsistent outputs;
          </li>
          <li>
            <strong>Outdated knowledge:</strong> The underlying model may have a
            training cutoff that predates the latest version of Angular, Angular
            Material, or related dependencies, resulting in deprecated API
            usage;
          </li>
          <li>
            <strong>Inconsistency:</strong> Generated code quality and
            completeness may vary across sessions, even for structurally similar
            prompts.
          </li>
        </ul>

        <h3 class="sub-heading">4.2 Company Not Liable for LLM Behaviour</h3>
        <p>
          Pythrust Technologies OPC Private Limited is not responsible for, and
          will not issue credits or refunds on account of:
        </p>
        <ul class="doc-list">
          <li>
            Credits consumed during an LLM inference call that returned output
            the user considers suboptimal, wrong, or incomplete;
          </li>
          <li>
            Credits consumed during repeated prompt iterations required to
            achieve a desired result;
          </li>
          <li>
            Costs incurred by the user in reviewing, testing, debugging, or
            correcting AI-generated code;
          </li>
          <li>
            Business losses, project delays, or technical debt attributable to
            use of generated code;
          </li>
          <li>
            Any outcome arising from the user acting on hallucinated or
            incorrect LLM output without independent verification.
          </li>
        </ul>

        <h3 class="sub-heading">4.3 Your Obligation to Verify</h3>
        <p>
          All generated code must be independently reviewed, tested, and
          validated by you before deployment to any environment. By consuming
          credits to generate code, you accept full responsibility for verifying
          its correctness, security, and suitability. See also Section 8 of our
          <a routerLink="/terms-and-conditions">Terms of Service</a>.
        </p>

        <div class="callout callout-neutral">
          <strong>📌 Practical Tip</strong>
          To get the best results and avoid unnecessary credit consumption,
          write specific, detailed prompts that include the framework version,
          component type, desired behaviour, and any constraints. Vague prompts
          are more likely to produce outputs requiring multiple iterations.
        </div>
      </section>

      <!-- ── R5: WHAT CREDITS COVER ── -->
      <section class="sec" id="r5">
        <span class="sec-num">Section 05</span>
        <h2 class="sec-heading">
          What Credits Cover &amp; How They Are Consumed
        </h2>
        <p>
          Platform credits are consumed based on the computational resources
          used during a generation request. You should understand how credits
          are consumed before submitting prompts:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Prompt submission:</strong> Credits are deducted at the
            point your prompt is submitted and processing begins —
            <em>not</em> upon receipt of a completed output. A deduction occurs
            even if the model returns a partial or low-quality result;
          </li>
          <li>
            <strong>Inference compute:</strong> Credits reflect the cost of
            running LLM inference on our GCP infrastructure, which is consumed
            regardless of output quality;
          </li>
          <li>
            <strong>Complex prompts:</strong> Prompts requesting multiple
            components, files, or architectural scaffolding consume more credits
            proportionally;
          </li>
          <li>
            <strong>Re-generation:</strong> Each re-generation or follow-up
            prompt constitutes a new inference call and consumes credits
            independently.
          </li>
        </ul>
        <div class="callout callout-warn">
          <strong>⚠ Credits Deducted at Submission</strong>
          Credits are deducted when your request enters our processing queue. If
          you close your browser, lose connectivity, or navigate away before
          output is displayed, the credits for that inference call are still
          consumed and are non-refundable.
        </div>
      </section>

      <!-- ── R6: SUBSCRIPTION CANCELLATION ── -->
      <section class="sec" id="r6">
        <span class="sec-num">Section 06</span>
        <h2 class="sec-heading">Subscription Cancellation</h2>
        <p>
          You may cancel your subscription at any time through your account
          billing settings. The following terms apply upon cancellation:
        </p>
        <ul class="doc-list">
          <li>
            Your subscription remains active and fully functional until the
            <strong>end of the current billing period</strong>;
          </li>
          <li>
            No refund is issued for the remaining days in the billing period;
          </li>
          <li>
            Unused plan-allocated credits expire at the end of the billing
            period and are forfeited — they are not carried forward and not
            redeemable for cash;
          </li>
          <li>
            Any separately purchased credit top-ups that have not expired by the
            end of your billing period are also forfeited upon cancellation — we
            do not extend their validity;
          </li>
          <li>
            Your account data is retained for 90 days post-cancellation in
            accordance with our
            <a routerLink="/privacy-policy">Privacy Policy</a>, after which it
            is deleted.
          </li>
        </ul>
        <p>
          If you choose to re-subscribe after cancellation, you do so on the
          pricing and plan terms in effect at the time of re-subscription. Prior
          plan rates or promotional pricing are not guaranteed.
        </p>
      </section>

      <!-- ── R7: BILLING ERRORS ── -->
      <section class="sec" id="r7">
        <span class="sec-num">Section 07</span>
        <h2 class="sec-heading">Genuine Billing Errors</h2>
        <p>
          The Company will investigate and rectify genuine, demonstrable billing
          errors. These are the <strong>only</strong> circumstances in which a
          monetary refund may be considered:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Duplicate charge:</strong> You were charged more than once
            for the same subscription period or credit purchase within the same
            billing cycle due to a verified payment processor or platform error;
          </li>
          <li>
            <strong>Incorrect amount charged:</strong> You were charged an
            amount materially different from the stated price displayed at the
            time of purchase, with no prior notice of a price change;
          </li>
          <li>
            <strong>Charge after confirmed cancellation:</strong> You were
            charged for a renewal period after a cancellation that was confirmed
            in writing by us before the renewal date.
          </li>
        </ul>
        <p>
          To report a billing error, email
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          within <strong>14 days</strong> of the erroneous charge with your
          transaction ID, the charged amount, and the correct amount you
          expected. We will investigate and respond within
          <strong>10 business days</strong>. Approved monetary refunds for
          billing errors are processed to the original payment method within
          <strong>14 business days</strong> of approval.
        </p>
        <div class="callout callout-neutral">
          <strong>📌 Note</strong>
          Dissatisfaction with the Platform, low usage, or a change of mind are
          not billing errors and are not grounds for a monetary refund.
        </div>
      </section>

      <!-- ── R8: INELIGIBLE SITUATIONS ── -->
      <section class="sec" id="r8">
        <span class="sec-num">Section 08</span>
        <h2 class="sec-heading">Situations That Do Not Qualify</h2>
        <p>
          For complete clarity, the following situations do
          <strong>not</strong> qualify for any monetary refund or platform
          credit consideration, regardless of how the request is framed:
        </p>
        <ul class="doc-list">
          <li>
            You purchased a subscription but did not use the Platform during the
            billing period;
          </li>
          <li>
            You were unaware of or did not read this Refund Policy or the Terms
            of Service before subscribing;
          </li>
          <li>You changed your mind after purchasing;</li>
          <li>
            You found an alternative tool or decided not to continue with
            Angular development;
          </li>
          <li>Generated code required debugging, editing, or iteration;</li>
          <li>
            The LLM produced hallucinated, incorrect, or suboptimal output (see
            Section 4);
          </li>
          <li>
            A specific Angular pattern, version, or library was not supported by
            the LLM at the time;
          </li>
          <li>
            You consumed credits on the beta platform at
            <strong>beta.angularize.dev</strong> (see Section 12);
          </li>
          <li>
            Your account was suspended or terminated due to a breach of our
            <a routerLink="/terms-and-conditions">Terms of Service</a>;
          </li>
          <li>
            You experienced slow generation times or temporary degraded
            performance that did not constitute a full outage;
          </li>
          <li>
            A promotional or discounted price expired and your subscription
            renewed at the standard rate;
          </li>
          <li>
            You shared your account with others, resulting in
            higher-than-expected credit consumption;
          </li>
          <li>
            You did not cancel before the auto-renewal date and the subscription
            renewed.
          </li>
        </ul>
      </section>

      <!-- ── R9: HOW TO REQUEST ── -->
      <section class="sec" id="r9">
        <span class="sec-num">Section 09</span>
        <h2 class="sec-heading">How to Submit a Request</h2>
        <p>
          To submit a credit consideration or billing error report, email us at
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          with the subject line
          <strong>"Refund / Credit Request — [Your Registered Email]"</strong>.
          Your request must include:
        </p>
        <ul class="doc-list">
          <li>Your registered email address and account username;</li>
          <li>Date and time of the prompt submission or billing event;</li>
          <li>
            The exact prompt you submitted (for credit consideration requests);
          </li>
          <li>
            Screenshots or screen recordings showing the error or absence of
            output;
          </li>
          <li>
            The request ID or session ID shown in the Platform interface, if
            available;
          </li>
          <li>Transaction ID and amount for billing error reports;</li>
          <li>
            A concise description of why you believe a credit or refund is
            warranted.
          </li>
        </ul>
        <p>
          <strong>Incomplete requests will not be processed.</strong> We may
          request additional evidence before beginning our review. The burden of
          proof lies with the requestor.
        </p>
        <div class="callout callout-warn">
          <strong>⚠ Time Limit</strong>
          Credit consideration requests must be submitted within
          <strong>7 days</strong> of the relevant generation event. Billing
          error reports must be submitted within <strong>14 days</strong> of the
          erroneous charge. Requests outside these windows will not be
          entertained under any circumstances.
        </div>
      </section>

      <!-- ── R10: REVIEW PROCESS ── -->
      <section class="sec" id="r10">
        <span class="sec-num">Section 10</span>
        <h2 class="sec-heading">Review Process &amp; Decision</h2>
        <h3 class="sub-heading">10.1 Internal Review</h3>
        <p>Upon receipt of a complete request, our team will:</p>
        <ul class="doc-list">
          <li>Verify your account identity and subscription status;</li>
          <li>
            Cross-reference server logs, generation records, and billing data
            against your submission;
          </li>
          <li>Evaluate the prompt quality and specificity where applicable;</li>
          <li>
            Assess whether the failure is attributable to a platform fault or
            LLM behaviour;
          </li>
          <li>
            Determine, in our sole discretion, whether a credit consideration is
            warranted.
          </li>
        </ul>
        <h3 class="sub-heading">10.2 Timeline</h3>
        <ul class="doc-list">
          <li>
            <strong>Acknowledgement:</strong> Within 2 business days of
            receiving your complete request;
          </li>
          <li>
            <strong>Decision:</strong> Within 14 business days of
            acknowledgement;
          </li>
          <li>
            <strong>Credit issuance</strong> (if approved): Within 3 business
            days of decision;
          </li>
          <li>
            <strong>Monetary refund</strong> (billing errors only, if approved):
            Within 14 business days of decision.
          </li>
        </ul>
        <h3 class="sub-heading">10.3 Finality of Decision</h3>
        <p>
          All decisions made by Pythrust Technologies OPC Private Limited
          regarding refund and credit requests are
          <strong>final and binding</strong>. We are not obliged to provide
          detailed reasons for our decision. Repeated requests for the same
          incident will not be re-reviewed.
        </p>
      </section>

      <!-- ── R11: CHARGEBACKS ── -->
      <section class="sec" id="r11">
        <span class="sec-num">Section 11</span>
        <h2 class="sec-heading">Chargebacks &amp; Payment Disputes</h2>
        <p>
          Initiating a chargeback or payment dispute with your bank or card
          issuer without first contacting us and allowing us to investigate
          constitutes a breach of these Terms and our
          <a routerLink="/terms-and-conditions">Terms of Service</a>.
        </p>
        <div class="callout callout-warn">
          <strong>⚠ Consequences of Chargebacks</strong>
          If you initiate an unjustified chargeback — i.e. a chargeback for a
          charge that does not constitute a genuine billing error — the
          following consequences apply:
        </div>
        <ul class="doc-list">
          <li>
            Your account will be <strong>immediately suspended</strong> pending
            investigation;
          </li>
          <li>
            We reserve the right to <strong>permanently terminate</strong> your
            account;
          </li>
          <li>
            We will submit all relevant evidence (including usage logs, terms
            acceptance records, and correspondence) to the payment processor and
            your bank to contest the chargeback;
          </li>
          <li>
            We may pursue the disputed amount through civil proceedings before
            the competent courts of <strong>Gurugram, Haryana, India</strong>;
          </li>
          <li>
            You will be liable for any fees, costs, and penalties imposed on us
            by the payment processor as a result of the chargeback.
          </li>
        </ul>
        <p>
          If you believe a charge was erroneous, please contact us at
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          first. We are committed to resolving genuine billing errors promptly
          and fairly.
        </p>
      </section>

      <!-- ── R12: BETA PLATFORM ── -->
      <section class="sec" id="r12">
        <span class="sec-num">Section 12</span>
        <h2 class="sec-heading">Beta Platform — No Credits or Refunds</h2>
        <div class="callout callout-warn">
          <strong>⚠ Beta Environment Notice</strong>
          This section applies to <strong>beta.angularize.dev</strong>.
        </div>
        <p>
          The beta environment is a pre-release platform provided for testing
          and evaluation. The following terms apply absolutely and without
          exception to the beta platform:
        </p>
        <ul class="doc-list">
          <li>
            Any credits consumed on <strong>beta.angularize.dev</strong> are
            <strong>non-refundable and non-creditable</strong> under all
            circumstances;
          </li>
          <li>
            The beta platform may experience higher rates of generation failure,
            errors, or unexpected behaviour compared to the production platform
            — this is expected and does not constitute grounds for any
            consideration;
          </li>
          <li>
            Features, credit consumption rates, and output quality on the beta
            platform may differ from production and are subject to change
            without notice;
          </li>
          <li>
            By using the beta platform, you explicitly accept the increased risk
            of suboptimal outcomes and waive any claim to credits or refunds
            arising from beta usage.
          </li>
        </ul>
        <p>
          If you require production-grade reliability, please use the primary
          platform at
          <a routerLink="/">angularize.dev</a>.
        </p>
      </section>

      <!-- ── R13: GOVERNING LAW ── -->
      <section class="sec" id="r13">
        <span class="sec-num">Section 13</span>
        <h2 class="sec-heading">Governing Law &amp; Disputes</h2>
        <p>
          This Policy is governed by and construed in accordance with the laws
          of the
          <strong>Republic of India</strong>. Any dispute arising out of or
          relating to this Policy or any billing matter shall be subject to the
          exclusive jurisdiction of the competent courts of
          <strong>Gurugram, Haryana, India</strong>.
        </p>
        <p>
          Before initiating any formal proceedings, both parties agree to
          attempt resolution in good faith within <strong>30 days</strong> of a
          written Notice of Dispute sent to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >. For full dispute resolution terms, refer to Section 17 of our
          <a routerLink="/terms-and-conditions">Terms of Service</a>.
        </p>
      </section>

      <!-- ── R14: CHANGES ── -->
      <section class="sec" id="r14">
        <span class="sec-num">Section 14</span>
        <h2 class="sec-heading">Changes to This Policy</h2>
        <p>
          We reserve the right to amend this Refund Policy at any time. For
          material changes, we will update the effective date above and notify
          registered users via email or a prominent notice on the Platform.
          Continued use of the Platform after changes take effect constitutes
          your acceptance of the revised Policy.
        </p>
        <p>
          We recommend reviewing this Policy periodically. The current version
          is always available at
          <a routerLink="/refund-policy">angularize.dev/refund-policy</a>.
        </p>
      </section>

      <!-- ── R15: CONTACT ── -->
      <section class="sec" id="r15">
        <span class="sec-num">Section 15</span>
        <h2 class="sec-heading">Contact Us</h2>
        <p>For all refund, credit, and billing queries:</p>
        <div class="contact-card">
          <div class="contact-item">
            <span class="lbl">Company</span>
            Pythrust Technologies OPC Private Limited
          </div>
          <div class="contact-item">
            <span class="lbl">Email</span>
            <a href="mailto:angularize.dev@gmail.com"
              >angularize.dev&#64;gmail.com</a
            >
          </div>
          <div class="contact-item">
            <span class="lbl">Subject Line</span>
            Refund / Credit Request — [Your Email]
          </div>
          <div class="contact-item">
            <span class="lbl">Address</span>
            WeWork, 5th Floor, DLF Two Horizon Centre,<br />
            Harizan Colony, DLF Phase 5, Sector 43,<br />
            Gurugram, Haryana 122002, India
          </div>
        </div>

        <div
          class="policy-links"
          role="navigation"
          aria-label="Related policies"
        >
          <span class="policy-links-label">Related Policies</span>
          <a routerLink="/terms-and-conditions" class="policy-link-pill"
            >Terms of Service →</a
          >
          <a routerLink="/privacy-policy" class="policy-link-pill"
            >Privacy Policy →</a
          >
        </div>
      </section>
    </main>
  </div>
  <!-- /rp-layout -->
</div>
<!-- /rp-wrap -->
